import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AggregationMode,
    AlarmList,
    ComponentDefinition,
    ConnectionPoint,
    Dashboard,
    DateRange,
    Dictionary,
    DigitalSetSimpleDictionary,
    Group,
    IAction,
    IGridsterOptions,
    IStringDateRange,
    IValueViewModel,
    Identifier,
    Miniature,
    ObjectAccessRights,
    RefreshClickData,
    TagStringValueDto,
    TagDefinition,
    WidgetValues,
    IStringDateRange2,
    ValidationPageConfiguration
} from '../../../models';
import { WidgetContainerBase } from '../../WidgetContainerBase';
import { IRootScope } from 'IRootScope';
import {
    format,
    IAuthorizationService,
    IConfigurationService,
    ICurrentApplicationService,
    IDashboardLoadService,
    IDashboardSeriesService,
    IDashboardService,
    IDateHelperFormatService,
    IDateRangeService,
    IDigitalSetHelperService,
    ILoggerService,
    INavigationHistoryService,
    IPrintConfiguration,
    ISmartGroupSelectService,
    IStringValuesHelperService,
    IStringValuesService,
    IUserSettingsService,
    IValidationPageConfigurationService,
    IWidgetDuplicationService,
    IwidgetService,
    orientation,
    SeriesDictionary
} from '../../../services';
import { IWidget, WidgetBase, IWidgetLayout, IWidgetFactory } from '../../../widgets';
import { Roles, guidEmpty, momentFormat, IPrintHelper, toDictionary, groupSelected } from '../../../common';
import * as Events from '../../../common/events';
import { IDashboardStateService } from '../dashboardState.service';
import { DashboardStateMapper } from '../DashboardStateMapper';
import { renamableWidgets } from '../../../widgets/widgetTypes';
import { ExtendedDashboardInfo } from '../../../services/data/ExtendedDashboardInfo';
import { Store } from '../../../stateManagement';
import { maxRows } from '../dashboardConstants';

class DashboardController extends WidgetContainerBase implements angular.IComponentController, DashboardStateMapper {
    static controllerId = 'Dashboard';

    accessRights: ObjectAccessRights[];
    actions: Dictionary<IAction>;
    alarmList: AlarmList;
    allValidated: boolean;
    beginDate: any;
    canEdit: boolean;
    canRename: boolean;
    connectionPointsDictionary: Dictionary<ConnectionPoint>;
    currentBeginDate: string;
    currentEndDate: string;
    currentInterval: IStringDateRange;
    dashboardGuid: string;
    dashboardName: string;
    dashboards: Dashboard[];
    data: WidgetValues[];
    // Injected via resolve
    digitalSets: DigitalSetSimpleDictionary;
    endDate: any;
    formController: angular.IFormController;
    format: string;
    gridsterPrintOptions: IGridsterOptions;
    group: Group;
    groupDictionary: Dictionary<Group>;
    groupGuids: string[];
    groups: Group[];
    groupsWithVP: Group[];
    hasExperimentalFeatures: boolean;
    hasManualInputRole: boolean = false;
    identifiers: Identifier[];
    initialRange: IStringDateRange2;
    isDatePickerDisabled: boolean;
    isLoading: boolean;
    isPlaying: boolean;
    isPropertiesMode: boolean;
    loadingMessage: string;
    miniatureList: Miniature[];
    missingReferenceGroup: boolean;
    pageBreaks: any[] = [];
    printConfiguration: IPrintConfiguration & { // This looks like the options of a PdfExportOptions. Should be merged
        dashboard?: { name: string; guid: string };
        beginDate?: string;
        endDate?: string;
        group?: Group;
    };
    referenceGroup: Group;
    selectedGroup: Group;
    specialRange: string;
    startAutoRefresh: boolean = false;
    store: Store;
    tagsDictionary: Dictionary<TagDefinition>;
    updateValidationPage: (type: string, referenceGroup: string, widgets: IWidget[]) => angular.IPromise<any>;
    stringValues: TagStringValueDto[];
    types: string[];
    validationEnabled: boolean;
    validationPageConfigurations: Dictionary<ValidationPageConfiguration>;
    viewModels: Dictionary<IValueViewModel>;

    private _crosshairPropagationGarbages: (() => void)[] = [];
    private _dashboardName;
    private _garbages: (() => void)[] = [];
    private _pristineWidgets: { [guid: string]: IWidgetLayout };

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$state',
        '$window',
        '$translate',
        '$timeout',
        'currentApplicationService',
        'dashboardService',
        'dateHelperFormatService',
        'dateRangeService',
        'loggerService',
        'widgetFactory',
        'dashboardLoadService',
        'navigationHistoryService',
        'authorizationService',
        'dashboardSeriesService',
        'widgetService',
        'printHelper',
        'userSettingsService',
        'dashboardStateService',
        'digitalSetHelperService',
        'configurationService',
        'smartGroupSelectService',
        'stringValuesService',
        'stringValuesHelperService',
        'validationPageConfigurationService',
        'widgetDuplicationService'
    ];
    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$window: angular.IWindowService,
        private _$translate: angular.translate.ITranslateService,
        private _$timeout: angular.ITimeoutService,
        private _currentApplicationService: ICurrentApplicationService,
        private _dashboardService: IDashboardService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateRangeService: IDateRangeService,
        private _loggerService: ILoggerService,
        private _widgetFactory: IWidgetFactory,
        private _dashboardLoadService: IDashboardLoadService,
        private _navigationHistoryService: INavigationHistoryService,
        private _authorizationService: IAuthorizationService,
        private _dashboardSeriesService: IDashboardSeriesService,
        private _widgetService: IwidgetService,
        private _printHelper: IPrintHelper,
        private _userSettingsService: IUserSettingsService,
        private _dashboardStateService: IDashboardStateService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _configurationService: IConfigurationService,
        private _smartGroupSelectService: ISmartGroupSelectService,
        private _stringValuesService: IStringValuesService,
        private _stringValuesHelperService: IStringValuesHelperService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _widgetDuplicationService: IWidgetDuplicationService
    ) {
        super(_$rootScope);
        this.hasManualInputRole = this._authorizationService.isInRoles(Roles.manualInputRoles);
        this.windowResize = this.windowResize.bind(this);
        this.format = this._dateHelperFormatService.getDateFormat();
    }

    $onInit() {
        this.loadingMessage = 'Initializing';
        this.isLoading = true;
        const app = this._currentApplicationService.getApplicationConfiguration();

        this.validationEnabled = app ? app.validationEnabled : false;
        this.configureGridster(this._$rootScope);
        this.dashboardGuid = this._$state.params['dashboardName'];
        this.canEdit = this._authorizationService.canEdit();
        this.initialRange = { beginDate: undefined, endDate: undefined };
        this.allValidated = false;
        // Set edit mode
        this.setEditMode(this._$rootScope.isEditMode);
        this.hasExperimentalFeatures = !!this._configurationService.configuration.useExperimentalFeatures;
        this.initMiniatureList();
        // Bind the resize event
        angular.element(this._$window).on('resize', this.windowResize);
        this._garbages.push(
            this._$rootScope.$on(groupSelected, ($event, selectedGroup: Group) => {
                this.selectedGroup = selectedGroup;
            }),
            // Handle edit mode state change
            this._$scope.$on(Events.toggleEditMode, () => {
                this._$rootScope.$broadcast(Events.treeUpdated);
                this.setEditMode(this._$rootScope.isEditMode);
            }),
            this._$scope.$watchGroup(
                [() => this.currentBeginDate, () => this.currentEndDate],
                () => (this.currentInterval = { startDate: this.currentBeginDate, endDate: this.currentEndDate })
            ),
            this._$scope.$watch(
                () => this.selectedItem,
                () => {
                    if (this.selectedItem === null) {
                        if (_.some(this.widgetList, (w) => (w as any).created)) {
                            this.widgetList = this.widgetList.filter((w) => !(w as any).created);
                            this.saveRestoreState(this.widgetList);
                        }
                    }
                }
            )
        );

        // First connect to store
        this.initStore()
            // Then load dashboard config not depending on current group
            .then(() => this.loadDashboardConfiguration())
            // Then reselect group and recompute series if required
            .then((data) => this.reselectGroup().then(() => data))
            // Finally init range (will trigger refresh but no watcher yet)
            .then((data) => this.initRanges(data.initialRange))
            // Force only one refresh
            .then((range) => this.onRefreshClick(range))
            .then(() => this._validationPageConfigurationService.getGroupsWithWidgets())
            .then((r) => {
                this.groupsWithVP = (r as Group[]).sort((a, b) => (a.uniqueName > b.uniqueName ? 1 : -1));
            })
            // Ensure watchers are only set after loading
            .then(() => this._$timeout(() => this.initWatchers()))
            .catch((r) => console.error(r))
            .finally(() => this._$timeout(() => (this.isLoading = false)));
    }

    $onDestroy() {
        angular.element(this._$window).off('resize', this.windowResize);
        this._garbages.forEach((g) => g());
        if (this._crosshairPropagationGarbages) {
            this._crosshairPropagationGarbages.forEach((c) => c());
        }
    }

    addIdentifierToAlarmList(alarmList: AlarmList): AlarmList {
        return alarmList;
    }

    selectItem(item: IWidget, event): void {
        if (this.isEditMode) {
            if (this.selectedItem !== item) {
                if (this.formController && this.formController.$dirty) {
                    this._loggerService.logWarning('Save or cancel changes before selecting another widget');
                } else {
                    this.setSelectedItem(item);
                }
            }
            if (event && event.preventDefault) {
                event.preventDefault();
            }
        }
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            this.selectItem(null, $event);
        }
    }

    private initMiniatureList(): void {
        let widgetsMiniature = this._widgetFactory.widgetList();
        if (!this.hasExperimentalFeatures) {
            widgetsMiniature = widgetsMiniature.filter((m) => !m.isExperimental);
        }
        this.miniatureList = widgetsMiniature;
    }

    private initStore(): angular.IPromise<void> {
        this.loadingMessage = 'Connecting to store';
        const connect = this._dashboardStateService.connect(this);
        this._garbages.push(connect.unsubscribe);
        return connect.onLoad;
    }

    private initWatchers(): void {
        const self = this;
        this._garbages.push(
            this._$scope.$on(Events.refreshClick, (e, d: RefreshClickData) => this.onRefreshClick(d)),
            this._$scope.$watch(
                () => this.selectedGroup,
                (n, o) => {
                    if (n !== o) {
                        this.updateSelectedGroup(n);
                    }
                }
            ),
            this._$scope.$watch(
                () => this.specialRange,
                () => {
                    this.setDashboardName(this._dashboardName);
                    this.updateOrCreateHistoryName(this.dashboardName);
                }
            ),
            this._$scope.$watch(
                () => this.widgetList,
                () => {
                    if (this.printConfiguration) {
                        try {
                            this.pageBreaks = this._printHelper.getPageBreaks(this.widgetList, this.gridsterOptions, this.printConfiguration);
                            if (this._crosshairPropagationGarbages.length > 0) {
                                this._crosshairPropagationGarbages.forEach((c) => c());
                            }
                            this._crosshairPropagationGarbages = this.registerCrosshairPropagationList(this.widgetList);
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }
            ),
            // Set gridster parameters when edit mode is on or off
            this._$scope.$on(Events.isEditMode, self.onIsEditMode()),
            // Select item if allowed
            this._$scope.$on(Events.itemSelectionAllowed, self.onItemSelectionAllowed()),
            // Toggle properties mode
            this._$scope.$on(Events.togglePropertiesMode, () => {
                this.isPropertiesMode = !this.isPropertiesMode;
            })
        );
        this.updateOrCreateHistoryName(this._dashboardName);
    }

    // Called at init only
    private reselectGroup(): angular.IPromise<void> {
        const defer = this._$q.defer<void>();
        const isByType = this.types?.length > 0;
        const hasGroups = this.groupGuids && this.groupGuids.length;

        // #38017 by default use reference group
        if (!this.group && !this.selectedGroup && (isByType || hasGroups) && this.referenceGroup) {
            this.selectedGroup = this.referenceGroup;
            this.updateSelectedGroup(this.selectedGroup, false);
            defer.resolve();
        }
        // reselect group with object from store using smart select
        else if (this.group && !this.selectedGroup) {
            if (isByType) {
                this._smartGroupSelectService
                    .smartGroupSelect(this.group.guid, this.types, this.groupDictionary)
                    .then((found) => {
                        if (found) {
                            this.selectedGroup = found;
                            // refresh series, but do not refresh data
                            // since load dashboard configuration will trigger
                            return this.updateSelectedGroup(found, false);
                        }
                    })
                    .finally(() => defer.resolve());
            } else if (hasGroups) {
                if (this.groupGuids.indexOf(this.group.guid) >= 0) {
                    this.selectedGroup = this.group;
                }
                defer.resolve();
            } else {
                defer.resolve();
            }
        }
        // default case
        else {
            defer.resolve();
        }

        return defer.promise;
    }

    private onRefreshClick(data?: RefreshClickData): angular.IPromise<any> {
        let beginDate: string;
        let endDate: string;
        if (data && !data.isLogbookEntryUpdated) {
            beginDate = data.beginDate ?? (this._dateRangeService.beginDate() as string);
            endDate = data.endDate ?? (this._dateRangeService.endDate() as string);
        } else {
            beginDate = this._dateRangeService.beginDate() as string;
            endDate = this._dateRangeService.endDate() as string;
        }
        const force = data ? !!data.force : false;
        return this.refresh(beginDate, endDate, force).finally(() => (this.isLoading = false));
    }

    private windowResize() {
        this.resizeGridster();
        this._$scope.$digest();
    }

    private updateOrCreateHistoryName(name: string) {
        const all = this._navigationHistoryService.getNavigationHistories();

        if (all && all.length > 0) {
            const last = all[0];

            if (last.page === 'dashboard' && (!this.selectedGroup || (this.selectedGroup && (!last.groupGuid || last.groupGuid === this.selectedGroup.guid)))) {
                this._navigationHistoryService.updateLastHistory(name, 'dashboard', this.selectedGroup ? this.selectedGroup.guid : undefined);
            } else {
                this._navigationHistoryService.setNavigationHistory({
                    name: name,
                    page: this._$state.current.name,
                    icon: this._$state.current.data.icon,
                    params: angular.copy(this._$state.params),
                    groupGuid: this.selectedGroup?.guid,
                    interval: {
                        begin: moment(this.beginDate),
                        end: moment(this.endDate)
                    }
                });
            }
        }
    }

    private crosshairPropagate(date: Date) {
        this.widgetList
            .filter((w) => !!w && !!w.configuration && !!w.configuration.options)
            .forEach((w) => {
                w.configuration.options.crosshairDate = date;
            });
    }

    private registerCrosshairPropagationList(widgetList: IWidget[]) {
        const types = ['tagsTrend', 'GroupStates', 'scatter', 'qualityPercentageEvolution'];
        const cleanFunctions = widgetList
            .filter((w) => !!w && !!w.configuration && !!w.configuration.options && types.indexOf(w.type) >= 0)
            .map((w) => {
                const cleanFunction = this._$scope.$watch(
                    () => w.configuration.options.crosshairDate,
                    (newValue: any, oldValue: any) => {
                        if (newValue !== oldValue) {
                            this.crosshairPropagate(newValue);
                        }
                    }
                );
                return cleanFunction;
            });
        return cleanFunctions;
    }

    private loadDashboardConfiguration(): angular.IPromise<ExtendedDashboardInfo> {
        this.loadingMessage = 'Loading dashboard configuration';
        return this._dashboardLoadService
            .load(this.dashboardGuid)
            .then((data) => {
                this.isDatePickerDisabled = data.isDatePickerDisabled;
                this.widgetList = data.widgetList;
                this.saveRestoreState(this.widgetList);

                const printConfiguration = data.configuration.printConfiguration;
                if (printConfiguration) {
                    this.printConfiguration = printConfiguration;
                } else {
                    this.printConfiguration = {
                        backgroundColor: null,
                        isHidingBorder: false,
                        printOptions: {
                            orientation: orientation.Portrait,
                            format: format.A3,
                            columns: 12,
                            rows: 100
                        }
                    };
                }

                this.printConfiguration.dashboard = {
                    name: data.baseName,
                    guid: this.dashboardGuid
                };

                this._$state.current.data.breadcrumbs.level4 = data.baseName;
                this.setDashboardName(data.baseName);
                this.types = data.types;
                this.groupGuids = data.groupGuids;
                this.missingReferenceGroup = this.types && this.types.length > 0 && (!data.referenceGroup || data.referenceGroup.guid === guidEmpty);

                this.referenceGroup = data.referenceGroup;
                this._dashboardName = data.baseName;
                return data;
                // DO not refresh, refresh button is triggered by initrange already
            })
            .catch((reason: { data: any; status: number }) => {
                if (reason) {
                    const { status } = reason;
                    if (status === 403) {
                        //Forbidden by API
                        this._$state.go('notFound');
                    } else if (reason.data) {
                        this._loggerService.logError(reason.data);
                    }
                } else {
                    this._loggerService.logError('Dashboard loading failed');
                }
                return null;
            });
    }

    private initRanges(range: DateRange): { beginDate: string; endDate: string } {
        const beginState = this._$state.params['beginDate'];
        const endState = this._$state.params['endDate'];

        // #39330: If both dates are empty and isAutoRefresh is true, start play mode.
        const isAutoRefresh = _.find(this.dashboards, (d) => d.guid === this.dashboardGuid && d.isAutoRefresh === true) !== undefined;
        this.startAutoRefresh = !beginState && !endState && isAutoRefresh;

        // #25217 : ensure dates are saved when coming from dashboard link
        if (beginState && endState) {
            this._userSettingsService.addSelectedDate(beginState, endState);
        }

        this.initialRange = {
            beginDate: beginState ? moment(beginState).format(momentFormat) : range && range.beginDateTime ? range.beginDateTime : null,
            endDate: endState ? moment(endState).format(momentFormat) : range && range.endDateTime ? range.endDateTime : null
        };
        if (range) {
            this.specialRange = this._$state.params['beginDate'] || this._$state.params['endDate'] ? null : this._$translate.instant(range.keyword);
        }
        this.beginDate = this.initialRange.beginDate;
        this.endDate = this.initialRange.endDate;
        return { beginDate: moment(this.beginDate, momentFormat).toISOString(), endDate: moment(this.endDate, momentFormat).toISOString() };
    }

    private resizeGridster() {
        this.gridsterOptions.margins = this._$window.innerWidth < 1500 ? [8, 8] : [14, 14];
    }

    private configureGridster($rootScope: angular.IRootScopeService) {
        this.gridsterOptions.rowHeight = 20;
        this.gridsterOptions.maxRows = maxRows;
        this.gridsterOptions.minRows = 36;
        this.gridsterOptions.sparse = true;
        this.resizeGridster();

        let startCol: number;
        let startRow: number;
        this.gridsterOptions.draggable.start = (event, uiWidget, $element: IWidget) => {
            if (!this.formController || !this.formController.$dirty) {
                if (!this.selectedItem || this.selectedItem !== $element) {
                    this.selectItem($element, event);
                }
            }
            startCol = $element.col;
            startRow = $element.row;
        };

        this.gridsterOptions.draggable.stop = (event, uiWidget, $element) => {
            if (startCol !== $element.col || startRow !== $element.row) {
                if (this.validateSize(this.gridsterOptions.maxRows, this.gridsterOptions.columns)) {
                    $rootScope.$broadcast(Events.widgetDragged, {
                        col: $element.col,
                        row: $element.row,
                        guid: $element.guid
                    });
                    startCol = null;
                    startRow = null;
                } else {
                    $element.col = startCol;
                    $element.row = startRow;
                }
            }
        };

        let startSizeX: number;
        let startSizeY: number;
        this.gridsterOptions.resizable.start = (event, uiWidget, $element: IWidget) => {
            this.resizeGridster();
            if (!this.selectedItem || this.selectedItem !== $element) {
                this.selectItem($element, event);
            }
            startSizeX = $element.sizeX;
            startSizeY = $element.sizeY;
        };

        this.gridsterOptions.resizable.stop = (event, uiWidget, $element) => {
            if (startSizeX !== $element.sizeX || startSizeY !== $element.sizeY) {
                if (this.validateSize(this.gridsterOptions.maxRows, this.gridsterOptions.columns)) {
                    $rootScope.$broadcast(Events.widgetResized, {
                        sizeX: $element.sizeX,
                        sizeY: $element.sizeY,
                        guid: $element.guid
                    });
                    startSizeX = null;
                    startSizeY = null;
                } else {
                    $element.sizeX = startSizeX;
                    $element.sizeY = startSizeY;
                }
            }
        };
    }

    validateSize(maxRows: number, maxCols: number): boolean {
        return !_.some(this.widgetList, (w) => w.row + w.sizeY > maxRows || w.col + w.sizeX > maxCols);
    }

    private updateSelectedGroup(group: Group, shouldRefreshData = true): angular.IPromise<SeriesDictionary<any>> {
        if (group) {
            const wasLoading = this.isLoading;
            this.setDashboardName(this._dashboardName);
            this.updateOrCreateHistoryName(this.dashboardName);
            this.isLoading = true;
            this.loadingMessage = 'Updating series';
            return this._dashboardSeriesService
                .getSeries(this.widgetList, this.referenceGroup, group)
                .then((seriesHash) => {
                    this.widgetList = this._dashboardLoadService.mapWidgetList(this.widgetList, seriesHash);
                    this.saveRestoreState(this.widgetList);
                    if (shouldRefreshData) {
                        return this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
                    }
                    return seriesHash;
                })
                .finally(() => {
                    this.isLoading = wasLoading;
                });
        }
    }

    private refreshApproved() {
        this.allValidated = false;
        if (!this.validationEnabled) {
            return;
        }
    }

    private setDashboardName(baseName: string) {
        let name = baseName;
        if (this.selectedGroup && this.selectedGroup.uniqueName) {
            name += ` - ${this.selectedGroup.uniqueName}`;
        }
        if (this.specialRange) {
            name += ` (${this.specialRange})`;
        }
        this.dashboardName = name;
    }

    private refresh(beginDate: string, endDate: string, force: boolean = false): angular.IPromise<any> {
        const wasLoading = this.isLoading;
        this.isLoading = true;
        this.loadingMessage = 'Loading data';
        this.printConfiguration.beginDate = beginDate;
        this.printConfiguration.endDate = endDate;
        this.printConfiguration.group = this.selectedGroup;
        const groupGuid = this.selectedGroup ? this.selectedGroup.guid : undefined;
        if (this.types && this.types.length > 0 && !groupGuid) {
            return this._$q.when({});
        }
        // Filter widgets if logbook entry has been added to reload only widgets with allow logbook entries
        // #41924: We can't filter like this, as when isLogbookEntryUpdated is true, the cache key (request.service.ts > cleanCachedPostValue)
        //         will be different and the cache won't be cleaned and set properly
        //         We deciced to remove optimization when adding logbook entry.
        // if (isLogbookEntryUpdated) {
        //     widgetsToRefresh = this.widgetList.filter(w => (w.configuration && w.configuration.showLogbook) || w.type === 'LogbookTable');
        // } else {
        //     widgetsToRefresh = this.widgetList;
        // }
        const widgetsToRefresh = this.widgetList;
        return this._stringValuesService
            .getAll()
            .then((response) => {
                this.stringValues = response;
                return this._dashboardLoadService
                    .refresh(widgetsToRefresh, beginDate, endDate, groupGuid, false, this.dashboardGuid, force)
                    .then((data) => {
                        data = this._dashboardLoadService.setIdentifiers(data, this.viewModels);
                        // TODO this should refactored to be moved on widget list component not here
                        // so that we would have access to each widget individually to map according
                        // aggregation config of widget (or worse series) individually
                        data = this._digitalSetHelperService.mapDigitalSetsValues(data, this.digitalSets, this.viewModels, AggregationMode.None);
                        data = this._stringValuesHelperService.mapStringValues(data, this.stringValues, this.viewModels, AggregationMode.None);
                        this.data = data;
                    })
                    .catch((reason) => {
                        if (reason && reason.exceptionType && reason.exceptionType === 'System.OutOfMemoryException') {
                            this._loggerService.logError('This amount of data cannot be retrieved. Try decreasing the period you are looking at.');
                        } else {
                            this._loggerService.logError('An unknown error occured while loading dashboards, check logs.');
                            console.error(reason);
                        }
                    });
            })
            .finally(() => {
                this.refreshApproved();
                this.isLoading = wasLoading;
            });
    }

    private setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode;
        this.gridsterOptions.draggable.enabled = isEditMode;
        this.gridsterOptions.resizable.enabled = isEditMode;
    }

    save(): angular.IPromise<any> {
        this.loadingMessage = 'Saving widgets';
        this.isLoading = true;
        const selectedItem = this.selectedItem;
        const dashboard = (Object as any).assign(
            {},
            _.find(this.dashboards, (d) => d.guid === this.dashboardGuid)
        );
        dashboard.configuration = (Object as any).assign({}, dashboard.configuration, { widgets: [] });
        this.widgetList.forEach((w: WidgetBase) => dashboard.configuration.widgets.push(WidgetBase.copy(w)));
        return this._dashboardStateService
            .updateDashboard(dashboard, true)
            .then((dashboards) => {
                this.widgetList.forEach((w) => delete w['created']);
                this._loggerService.logSuccess('Dashboard saved.');
                this.formController.$setPristine();
                this.loadingMessage = 'Refreshing dashboard';
                const d = _.find(dashboards, (d) => d.guid === this.dashboardGuid);
                return this.refreshWidgetListTheNewWay(d);
            })
            .then(() => {
                if (selectedItem) {
                    this.selectItem(this.widgetList.filter((w) => w.guid === selectedItem.guid)[0], null);
                }
            })
            .catch(() => this._loggerService.logError('Failed to save dashboard.'))
            .finally(() => (this.isLoading = false));
    }

    cancel(): angular.IPromise<any> {
        this.selectedItem = null;
        this.widgetList.forEach((widget) => {
            const pristine = this._pristineWidgets[widget.guid];
            if (pristine) {
                for (const key of Object.keys(pristine)) {
                    widget[key] = pristine[key];
                }
            }
        });
        return this._$q.resolve();
    }

    deleteItem(guid: string): void {
        const toDelete = _.find(this.widgetList, (w) => w.guid === guid);

        if (toDelete && (toDelete as any).created) {
            this.widgetList = this.widgetList.filter((w) => w.guid !== guid);
            this.saveRestoreState(this.widgetList);
            this.setSelectedItem(null);
        } else {
            this.isLoading = true;
            this.loadingMessage = 'Deleting widget';
            let _data;
            const dashboardGuid = this._$state.params['dashboardName'];
            this._dashboardService
                .deleteWidget(dashboardGuid, guid)
                .then((data) => {
                    this._loggerService.logSuccess('Widget successfully deleted');
                    _data = data;
                    return this._dashboardStateService.postDeleteWidget(dashboardGuid);
                })
                .then((dashboard) => {
                    return this.refreshWidgetListTheNewWay(_data);
                })
                .catch(() => this._loggerService.logError('Failed to delete widget'))
                .finally(() => (this.isLoading = false));
        }
    }

    /**
     * Updates the widget list after a change (add - update - delete).
     * As the operations at this level do not change series or data, previously loaded info could be reused.
     */
    private refreshWidgetListTheNewWay(updatedDashboard: Dashboard): angular.IPromise<any> {
        this.loadingMessage = 'Reloading series';
        const widgets = updatedDashboard.configuration.widgets.map((w) => w);
        return this._dashboardSeriesService
            .mapSeries(widgets, this.viewModels, this.groupDictionary, toDictionary(this.dashboards), this.alarmList, this.referenceGroup, this.selectedGroup)
            .then((data) => {
                this.loadingMessage = 'Mapping widget list';
                const widgetList = this._dashboardLoadService.mapWidgetList(widgets, data);
                this.setSelectedItem(null);
                widgetList.forEach((w) => {
                    const found = _.find(this.widgetList, (d) => d.guid === w.guid);
                    if (found?.data) {
                        w.data = found.data;
                    }
                    if (found?.executedQueries) {
                        w.executedQueries = found.executedQueries;
                        w.executedQueriesMap = found.executedQueriesMap;
                    }
                });
                this.widgetList = widgetList;
                this.saveRestoreState(this.widgetList);
            })
            .catch(() => {
                this._loggerService.logError('Failed to map series');
            });
    }

    duplicateItem(guid: string): void {
        const widget = _.find(this.widgetList, (w) => w.guid === guid);

        this.canRename = renamableWidgets.indexOf(widget.type) >= 0;
        $('#duplicateWidgetModal').modal('show');
        // Might require a refresh
    }

    reload(dashboard: Dashboard) {
        if (dashboard.guid === this.dashboardGuid) {
            this.isLoading = true;
            this._dashboardLoadService
                .loadInfo(dashboard.guid)
                .then((data) => {
                    this.widgetList = data.widgetList;
                    this.saveRestoreState(this.widgetList);
                    this.setSelectedItem(this.widgetList[this.widgetList.length - 1]);
                    return this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
                })
                .finally(() => (this.isLoading = false));
        }
    }

    print() {
        const url = this.printConfiguration?.isUseNewPrintSystem ? 'scaledPrintPreview' : 'printDashboard';
        this._$state.go(url, {
            guid: this.dashboardGuid,
            groupGuid: this.selectedGroup?.guid,
            beginDate: this._dateRangeService.beginDate() as string,
            endDate: this._dateRangeService.endDate() as string
        });
    }

    drop(data: { itemId: string }) {
        const widget = this._widgetFactory.createWidget(data.itemId);
        widget.name = this._$translate.instant(widget.name);
        if (widget.sanitize) {
            widget.sanitize();
        }
        if (widget) {
            (widget as any).created = true;
            widget.row = this.getWidgetRow(widget, this.widgetList);
            this.widgetList.push(widget);
            this.setSelectedItem(widget);
            // Set the form dirty to force saving
            this._$timeout(() => {
                this.formController.$setDirty();
            });
        } else {
            this._loggerService.logError('Invalid widget type');
        }
    }

    private getWidgetRow(widget: any, widgetList: IWidget[]): number {
        let row: number = 0;
        widgetList.forEach((w) => {
            if (w.row + w.sizeY > row) {
                row = w.row + w.sizeY;
            }
        });
        return row;
    }

    private saveRestoreState(list: IWidget[]) {
        const layouts = {};
        list.forEach((item) => {
            const { name, sizeX, sizeY, col, row, backgroundColor, hideTitle, dashboardLink, isTitleHidden } = item;
            const l = { name, sizeX, sizeY, col, row, backgroundColor, hideTitle, dashboardLink, isTitleHidden } as IWidgetLayout;
            layouts[item.guid] = l;
        });
        this._pristineWidgets = layouts;
    }

    addWidgetToPage(groupGuid: string, widget: IWidget) {
        this.isLoading = true;
        return this._widgetDuplicationService
            .addWidgetToPage(groupGuid, widget, this.groupDictionary, this.validationPageConfigurations, this.viewModels, this.updateValidationPage)
            .finally(() => {
                this.isLoading = false;
            });
    }

    duplicate(dashboardGuid: string, newName: string, seriesRename: any[], groupsRename: any[]): angular.IPromise<any> {
        return this._dashboardStateService
            .loadDashboard(dashboardGuid)
            .then((dashboardToClone) => {
                return this._widgetService.cloneDashboard(dashboardToClone, newName, seriesRename, groupsRename);
            })
            .then((data) => {
                const mappings = data.mappingResults;
                mappings.forEach((m) => {
                    if (m.mappings.irreplacible && m.mappings.irreplacible.length) {
                        m.mappings.irreplacible.forEach((mapping) => {
                            this._loggerService.logWarning(
                                `Series ${mapping.newVm.name} does not exist, series ${mapping.originalVm.name} kept in place in widget ${m.widget.name}.`
                            );
                        });
                    }
                });
                return this._dashboardStateService.addDashboard(data.copy, true);
            })
            .then((data) => {
                // return data;
                return this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
            });
    }

    onCancelLogbookEntry() {
        this.disableLogbookEdition();
    }

    onSaveLogbookEntry() {
        this.disableLogbookEdition();
    }

    private disableLogbookEdition() {
        (this.widgetList as any[])
            .filter((w) => w.isSelectingLogbook)
            .forEach((w) => {
                w.isSelectingLogbook = false;
                w.select();
            });
    }
}

export const wtDashboard: ComponentDefinition = {
    componentId: 'wtDashboard',
    controller: DashboardController,
    template: require('./dashboard.component.html'),
    controllerAs: 'vm',
    bindings: {
        group: '<?'
    }
};
